/**
 * Value sync for snippets/rifugio-date-field.liquid.
 *
 * The <input type="date"> owns the value — it carries the form field name and
 * submits on its own. This module only mirrors that value in and out of Cally's
 * <calendar-date>, and reveals the calendar trigger once the custom element has
 * upgraded.
 *
 * Everything is delegated from `document`, so fields added later (theme editor
 * re-render, or any markup swapped in after load) are picked up without
 * re-running anything. That matters because a module executes only once per
 * URL: per-element binding at load would silently miss re-rendered sections.
 *
 * Cally itself is loaded by its own <script> tag alongside this one, NOT by a
 * relative `import` from here. A bare `import './rifugio-cally.js'` resolves
 * without Shopify's ?v= cache-buster, so a browser could hold a stale copy
 * across a version bump. Load order does not matter either way — every entry
 * point below is guarded by customElements.whenDefined or by delegation.
 */

const FIELD = '[data-rf-datefield]';

/* Cally is `min`-aware but will still happily be handed an out-of-range value
   by a typed-in input. Guard rather than trust: an invalid `value` makes the
   calendar render an empty grid with no indication why. */
const isValidISODate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));

const popoverFor = (el) => el.closest(FIELD)?.querySelector('.rf-datefield__popover');

/* Open, but never toggle. A click anywhere outside the popover — the input
   included — light-dismisses it first, so by the time this runs the popover is
   already closed and togglePopover() would reopen a box the user just asked to
   close. Checking :popover-open makes the second click a no-op instead. */
const openCalendar = (el) => {
  const popover = popoverFor(el);
  if (popover && !popover.matches(':popover-open')) popover.showPopover();
};

/* Enhance only once <calendar-date> has upgraded. Until then the native picker
   on the input is the entire experience, which is the fallback this whole
   snippet is designed around — so nothing below may run early. */
customElements.whenDefined('calendar-date').then(() => {
  document.querySelectorAll(FIELD).forEach((field) => {
    const toggle = field.querySelector('[data-rf-datefield-toggle]');
    const input = field.querySelector('[data-rf-datefield-input]');
    if (toggle) toggle.hidden = false;

    /* readonly makes the field a picker target rather than a text field. Two
       reasons it is not merely cosmetic: the native indicator is hidden by CSS
       once enhanced, so a click would otherwise do nothing at all; and on
       mobile a tappable date input opens the OS picker, which would fight our
       popover for the same gesture. readonly inputs still submit and still
       honour `required`. It is set here, never in the markup, so a browser
       that fails to upgrade the element keeps a fully editable native input. */
    if (input) input.readOnly = true;

    /* focusday only fires on interaction, so the first paint is ours. */
    const calendar = field.querySelector('[data-rf-datefield-calendar]');
    if (calendar) syncHeading(calendar);
  });
});

/* Click the field itself, not just the icon. */
document.addEventListener('click', (event) => {
  const input = event.target.closest?.('[data-rf-datefield-input]');
  if (input?.readOnly) openCalendar(input);
});

/* Keyboard parity: the input is in the tab order before the toggle, so Enter,
   Space and ArrowDown open the calendar from where focus already is. */
document.addEventListener('keydown', (event) => {
  const input = event.target.closest?.('[data-rf-datefield-input]');
  if (!input?.readOnly) return;
  if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown') return;
  event.preventDefault(); /* Enter would submit the form; Space would scroll. */
  openCalendar(input);
});

/* Keep the slotted "August 2026" heading in step with the visible page.

   Cally's own calendar-date heading is the year alone and each calendar-month
   prints its month name separately — a split that exists for multi-month views
   and reads badly with one month. We replace it, which means we own keeping it
   current across paging. */
const syncHeading = (calendar) => {
  const target = calendar.querySelector('[data-rf-datefield-heading]');
  if (!target) return;
  const iso = calendar.focusedDate || calendar.value;
  const date = isValidISODate(iso) ? new Date(`${iso}T00:00:00Z`) : new Date();
  target.textContent = new Intl.DateTimeFormat(calendar.getAttribute('locale') || undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC', /* the ISO strings Cally emits are UTC-anchored. */
  }).format(date);
};

/* `focusday` is dispatched on calendar-date without `bubbles`, so it never
   reaches document during the bubble phase. Capture still runs — a non-bubbling
   event is fully propagated on the way down to its target. */
document.addEventListener('focusday', (event) => {
  const calendar = event.target;
  if (calendar.matches?.('[data-rf-datefield-calendar]')) syncHeading(calendar);
}, true);

/* input → calendar, on open. Reading at open time rather than caching means a
   value typed straight into the input is always what the calendar shows. */
document.addEventListener('toggle', (event) => {
  const popover = event.target;
  if (event.newState !== 'open' || !popover.matches?.('.rf-datefield__popover')) return;

  const field = popover.closest(FIELD);
  const input = field?.querySelector('[data-rf-datefield-input]');
  const calendar = popover.querySelector('[data-rf-datefield-calendar]');
  if (!input || !calendar) return;

  calendar.value = isValidISODate(input.value) ? input.value : '';
  syncHeading(calendar);
  /* focus() is Cally's own method, not HTMLElement's — it moves focus onto the
     focused day inside the shadow root. Deferred to the next frame: at `toggle`
     the popover is still mid-transition and not yet focusable. */
  requestAnimationFrame(() => calendar.focus?.());
}, true); /* capture: the `toggle` event does not bubble. */

/* calendar → input. `change` is what Cally fires on selection.

   Capture, like `focusday` above: calendar-date builds both events without
   `bubbles`, so neither reaches document on the way up. Registered on the
   bubble phase this listener silently never runs and the chosen date never
   reaches the input — which is exactly how it failed the first time. */
document.addEventListener('change', (event) => {
  const calendar = event.target;
  if (!calendar.matches?.('[data-rf-datefield-calendar]')) return;

  const field = calendar.closest(FIELD);
  const input = field?.querySelector('[data-rf-datefield-input]');
  if (!input || !isValidISODate(calendar.value)) return;

  input.value = calendar.value;
  /* Let anything watching the real field react — Shopify form validation and
     any future listener bind to the input, not to the calendar. */
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));

  field.querySelector('.rf-datefield__popover')?.hidePopover?.();
  /* Without this, closing the popover destroys the focused element's context
     and focus falls back to <body>, stranding keyboard users mid-form. */
  input.focus();
}, true);
